import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LangContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success(t('success'));
      navigate('/app');
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-[#202020]">
        <img
          src="https://images.unsplash.com/photo-1761095870661-c4ae15cac605?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjByZXRhaWwlMjBzdG9yZSUyMGludGVyaW9yJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3NzI3Nzc0Nzd8MA&ixlib=rb-4.1.0&q=85"
          alt="Store"
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="relative z-10 flex flex-col justify-end p-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-[#EB7B44] flex items-center justify-center">
              <span className="text-white font-bold text-2xl" style={{ fontFamily: 'Manrope' }}>S</span>
            </div>
            <span className="text-3xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>Sellmu</span>
          </div>
          <p className="text-white/80 text-lg max-w-md" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            {t('pos_system')} — {t('manage_business')}
          </p>
        </div>
      </div>

      {/* Right - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 bg-[#F6F5F5]">
        <div className="w-full max-w-[420px] animate-fade-in">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2 mb-10">
            <div className="w-10 h-10 rounded-lg bg-[#EB7B44] flex items-center justify-center">
              <span className="text-white font-bold text-xl" style={{ fontFamily: 'Manrope' }}>S</span>
            </div>
            <span className="text-2xl font-bold text-[#202020]" style={{ fontFamily: 'Manrope' }}>Sellmu</span>
          </div>

          <h1 className="text-3xl font-bold text-[#202020] mb-2" style={{ fontFamily: 'Manrope' }}>
            {t('login')}
          </h1>
          <p className="text-[#64584F] mb-8">{t('manage_business')}</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#202020] font-medium">{t('email')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="admin@sellmu.com"
                required
                className="h-12 bg-white border-[#CACACC] focus:border-[#EB7B44] focus:ring-1 focus:ring-[#EB7B44] rounded-lg"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#202020] font-medium">{t('password')}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="********"
                  required
                  className="h-12 bg-white border-[#CACACC] focus:border-[#EB7B44] focus:ring-1 focus:ring-[#EB7B44] rounded-lg pr-12"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A6A2A0] hover:text-[#64584F]"
                >
                  {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#EB7B44] hover:bg-[#D96A35] text-white font-semibold rounded-lg text-base transition-all active:scale-[0.98]"
              data-testid="login-submit-btn"
            >
              {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : t('login')}
            </Button>
          </form>

          <p className="text-center mt-6 text-sm text-[#64584F]">
            {t('dont_have_account')}{' '}
            <Link to="/register" className="text-[#EB7B44] font-semibold hover:underline" data-testid="goto-register-link">
              {t('register')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
